/**
 * Channel Feedback Quality page.
 *
 * Buckets every channel we've surfaced into GOOD (net-positive parent
 * feedback), BAD (net-negative), or TBD (flagged but no verdict yet),
 * combining per-video parent votes (rolled up to the channel) with
 * explicit FOLLOW/BLOCK channel actions. Lets an operator see how much
 * usable signal parental review is actually producing — and on which
 * channels. Filterable to one kid or all combined.
 */
import React, {useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {useGetChannelFeedbackQuality} from '../../api/graphql_admin_channel_feedback';
import {useGetAllYouTubeProfilesLite} from '../../api/graphql_failed_tasks';
import {usePageTitle} from '../../hooks/usePageTitle';
import {cardSx, pageSx, pageTitleSx, pcolors} from '../../theme/planapusTheme';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

interface Row {
  channelId: string;
  channelName: string;
  avatarUrl?: string | null;
  yesVotes: number;
  noVotes: number;
  skipVotes: number;
  followed: boolean;
  blocked: boolean;
  netScore: number;
}

const youtubeUrl = (channelId: string) =>
  `https://www.youtube.com/channel/${channelId}`;

/** One fixed-width vote cell so the columns line up across every row. */
function Stat({arrow, count, color}: {arrow: string; count: number; color: string}) {
  const on = count > 0;
  return (
    <Box
      sx={{
        width: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.25,
        fontVariantNumeric: 'tabular-nums',
        fontSize: 12.5,
        color: on ? color : pcolors.textDisabled,
        fontWeight: on ? 600 : 400,
      }}
    >
      <span>{arrow}</span>
      <span>{count}</span>
    </Box>
  );
}

function ChannelRow({r, last}: {r: Row; last: boolean}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '28px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        borderBottom: last ? 'none' : `1px solid ${pcolors.borderLight}`,
        '&:hover': {bgcolor: pcolors.rowHover},
      }}
    >
      <Avatar
        src={r.avatarUrl || undefined}
        sx={{width: 24, height: 24, fontSize: 12, bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
      >
        {r.channelName.slice(0, 1).toUpperCase()}
      </Avatar>

      <Stack direction="row" alignItems="center" spacing={0.75} sx={{minWidth: 0}}>
        <Link
          href={youtubeUrl(r.channelId)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: pcolors.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={r.channelName}
        >
          {r.channelName}
        </Link>
        {r.followed && (
          <Tooltip title="System recommended subscribing — channel scored beneficial (≥75% good videos, <5% restricted).">
            <Chip
              label="Follow"
              size="small"
              sx={{height: 17, fontSize: 10, fontWeight: 600, bgcolor: pcolors.green[50], color: pcolors.green[700]}}
            />
          </Tooltip>
        )}
        {r.blocked && (
          <Tooltip title="System recommended blocking — channel scored harmful (≥25% restricted videos).">
            <Chip
              label="Block"
              size="small"
              sx={{height: 17, fontSize: 10, fontWeight: 600, bgcolor: pcolors.red[50], color: pcolors.red[700]}}
            />
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Tooltip title="Good-fit votes"><Box component="span"><Stat arrow="↑" count={r.yesVotes} color={pcolors.green[600]} /></Box></Tooltip>
        <Tooltip title="Bad-fit votes"><Box component="span"><Stat arrow="↓" count={r.noVotes} color={pcolors.red[600]} /></Box></Tooltip>
        <Tooltip title="Skipped"><Box component="span"><Stat arrow="~" count={r.skipVotes} color={pcolors.textSecondary} /></Box></Tooltip>
      </Stack>
    </Box>
  );
}

function Bucket({
  title,
  hint,
  accent,
  countBg,
  rows,
}: {
  title: string;
  hint: string;
  accent: string;
  countBg: string;
  rows: Row[];
}) {
  return (
    <Box sx={{...cardSx, flex: 1, minWidth: 300, overflow: 'hidden'}}>
      <Box sx={{px: 1.5, py: 1.25, borderBottom: `1px solid ${pcolors.border}`}}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: accent}} />
            <Typography sx={{fontSize: 13, fontWeight: 700, color: pcolors.textPrimary, textTransform: 'uppercase', letterSpacing: 0.4}}>
              {title}
            </Typography>
          </Stack>
          <Chip
            label={rows.length}
            size="small"
            sx={{height: 20, fontSize: 12, fontWeight: 700, bgcolor: countBg, color: accent}}
          />
        </Stack>
        <Typography sx={{fontSize: 11, color: pcolors.textSecondary, mt: 0.5}}>{hint}</Typography>
      </Box>
      <Box sx={{maxHeight: 560, overflowY: 'auto'}}>
        {rows.length === 0 ? (
          <Typography sx={{fontSize: 12, color: pcolors.textMuted, fontStyle: 'italic', px: 1.5, py: 1.5}}>
            None
          </Typography>
        ) : (
          rows.map((r, i) => <ChannelRow key={r.channelId} r={r} last={i === rows.length - 1} />)
        )}
      </Box>
    </Box>
  );
}

export default function AdminChannelFeedback({client}: Props) {
  usePageTitle('Channel Feedback');
  const [profileId, setProfileId] = useState<string>('');
  const {data: profData} = useGetAllYouTubeProfilesLite(client);
  const {data, loading} = useGetChannelFeedbackQuality(client, profileId || null);

  const profiles = (profData?.getAllYouTubeProfiles ?? []).filter(Boolean) as {
    id: string;
    name: string;
  }[];
  const q = data?.getChannelFeedbackQuality;

  return (
    <Box sx={pageSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 0.5}}>
        <Box>
          <Typography sx={pageTitleSx}>Channel Feedback Quality</Typography>
          <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5, maxWidth: 720}}>
            Channels bucketed by parental-review signal — per-video votes rolled up to the
            channel plus explicit follow/block actions. Shows how much usable signal review
            is producing. <strong>↑</strong> good-fit · <strong>↓</strong> bad-fit ·{' '}
            <strong>~</strong> skipped · <strong>Follow</strong> = system recommended
            subscribing (beneficial channel).
          </Typography>
        </Box>
        <Select
          size="small"
          value={profileId}
          displayEmpty
          onChange={e => setProfileId(e.target.value)}
          sx={{minWidth: 170, ml: 2, flexShrink: 0}}
        >
          <MenuItem value="">All kids</MenuItem>
          {profiles.map(p => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {loading && !q ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress size={20} />
        </Box>
      ) : q ? (
        <Stack direction={{xs: 'column', md: 'row'}} spacing={2} alignItems="stretch" sx={{mt: 2}}>
          <Bucket
            title="Good"
            hint="Net-positive feedback — keep recommending."
            accent={pcolors.green[600]}
            countBg={pcolors.green[50]}
            rows={q.good as Row[]}
          />
          <Bucket
            title="Bad"
            hint="Net-negative feedback — pull back."
            accent={pcolors.red[600]}
            countBg={pcolors.red[50]}
            rows={q.bad as Row[]}
          />
          <Bucket
            title="TBD"
            hint="No verdict yet, or feedback nets to zero."
            accent={pcolors.amber[700]}
            countBg={pcolors.amber[50]}
            rows={q.tbd as Row[]}
          />
        </Stack>
      ) : (
        <Typography sx={{color: pcolors.textMuted, mt: 2}}>No data.</Typography>
      )}
    </Box>
  );
}
